/**
 * @file pages/MomentDetail.js
 * @description Page de détail d'un moment d'étude avec pensées personnelles
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MOMENT_DATA = {
  romains: {
    name: 'La connaissance du livre de Romains',
    book: 'Romains',
    icon: '✦',
    intro: `L'épître aux Romains est souvent considérée comme le chef-d'œuvre théologique de l'apôtre Paul. 
    Rédigée vers 57 après J.-C., elle expose de manière systématique la doctrine du salut par la grâce, 
    par le biais de la foi en Jésus-Christ.`,
    themes: [
      { title: 'La condition humaine', ref: 'Romains 1-3', desc: 'Tous ont péché et sont privés de la gloire de Dieu.' },
      { title: 'La justification par la foi', ref: 'Romains 3-5', desc: 'La justice divine offerte gratuitement par grâce.' },
      { title: 'La sanctification', ref: 'Romains 6-8', desc: 'La vie dans l\'Esprit et la liberté du péché.' },
      { title: 'Le dessein d\'Israël', ref: 'Romains 9-11', desc: 'La souveraineté de Dieu dans l\'histoire du salut.' },
      { title: 'L\'éthique chrétienne', ref: 'Romains 12-16', desc: 'Vivre en sacrifice vivant pour Dieu.' },
    ],
    verses: [
    { text: "Je n'ai pas honte de l'Évangile, car c'est une puissance de Dieu pour le salut de quiconque croit.", ref: 'Romains 1:16' },
    { text: "Tous ont péché et sont privés de la gloire de Dieu.", ref: 'Romains 3:23' },
    { text: "Le salaire du péché c'est la mort, mais le don gratuit de Dieu c'est la vie éternelle.", ref: 'Romains 6:23' },
    { text: "Il n'y a donc maintenant aucune condamnation pour ceux qui sont en Jésus-Christ.", ref: 'Romains 8:1' },
    { text: "Toutes choses concourent au bien de ceux qui aiment Dieu.", ref: 'Romains 8:28' },
    { text: "Si tu confesses de ta bouche le Seigneur Jésus et si tu crois en ton coeur que Dieu l'a ressuscité des morts, tu seras sauvé.", ref: 'Romains 10:9' },
    { text: "La foi vient de ce qu'on entend, et ce qu'on entend vient de la parole de Christ.", ref: 'Romains 10:17' },
    { text: "Ne vous conformez pas au siècle présent, mais soyez transformés par le renouvellement de l'intelligence.", ref: 'Romains 12:2' },
    { text: "Que l'amour soit sans hypocrisie. Ayez le mal en horreur, attachez-vous au bien.", ref: 'Romains 12:9' },
    { text: "Ne te laisse pas vaincre par le mal, mais surmonte le mal par le bien.", ref: 'Romains 12:21' },
    { text: "La ou le péché a abondé, la grâce a surabondé.", ref: 'Romains 5:20' },
    { text: "Etant justifiés par la foi, nous avons la paix avec Dieu par notre Seigneur Jésus-Christ.", ref: 'Romains 5:1' },
  ],
  },
};

const MomentDetail = () => {
  const { momentId } = useParams();
  const navigate = useNavigate();
  const { getThoughts, saveThought, deleteThought, user } = useAuth();

  const [thoughts, setThoughts] = useState([]);
  const [newThought, setNewThought] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingThoughts, setLoadingThoughts] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const moment = MOMENT_DATA[momentId];
  const randomVerse = moment?.verses
  ? moment.verses[Math.floor(Math.random() * moment.verses.length)]
  : null;

  useEffect(() => {
    if (!moment) {
      navigate('/moments');
      return;
    }
    fetchThoughts();
  }, [momentId]);

  const fetchThoughts = async () => {
    setLoadingThoughts(true);
    try {
      const data = await getThoughts(momentId);
      setThoughts(data);
    } catch {
      setError('Impossible de charger les pensées.');
    } finally {
      setLoadingThoughts(false);
    }
  };

  const handleSave = async () => {
    if (!newThought.trim()) return;
    setSaving(true);
    setError('');
    try {
      const thought = await saveThought(momentId, moment.name, newThought.trim());
      setThoughts([thought, ...thoughts]);
      setNewThought('');
      setSuccess('✓ Pensée enregistrée avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erreur lors de l\'enregistrement de la pensée.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (thoughtId) => {
    try {
      await deleteThought(thoughtId);
      setThoughts(thoughts.filter(t => t._id !== thoughtId));
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!moment) return null;

  return (
    <>
      <style>{`
        .detail-page {
          min-height: 100vh;
          padding: 80px 0 80px;
          background: linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
        }
        .detail-hero {
          padding: 60px 24px;
          text-align: center;
          background: rgba(169, 134, 103, 0.05);
          border-bottom: 1px solid rgba(169, 134, 103, 0.1);
          position: relative;
          overflow: hidden;
          animation: fadeInUp 0.5s ease;
        }
        .detail-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 60% 100% at 50% 50%, rgba(169, 134, 103, 0.08), transparent 70%);
        }
        .detail-back {
          position: absolute;
          top: 24px;
          left: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(240, 234, 226, 0.6);
          font-size: 0.9rem;
          transition: color 0.2s;
          background: none;
          border: none;
          cursor: pointer;
          z-index: 2;
        }
        .detail-back:hover { color: #A98667; }
        .detail-hero-icon {
          width: 72px;
          height: 72px;
          background: rgba(169, 134, 103, 0.15);
          border: 1px solid rgba(169, 134, 103, 0.3);
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8rem;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .detail-book {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #A98667;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }
        .detail-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          color: #FFFFFF;
          max-width: 700px;
          margin: 0 auto 20px;
          line-height: 1.2;
          position: relative;
          z-index: 1;
        }
        .detail-intro {
          color: rgba(240, 234, 226, 0.65);
          max-width: 680px;
          margin: 0 auto;
          font-size: 1rem;
          line-height: 1.8;
          position: relative;
          z-index: 1;
        }
        /* Verse block */
        .verse-block {
          background: rgba(169, 134, 103, 0.08);
          border-left: 3px solid #A98667;
          border-radius: 0 12px 12px 0;
          padding: 24px 28px;
          margin: 40px auto;
          max-width: 600px;
        }
        .verse-text {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 1.25rem;
          color: rgba(240, 234, 226, 0.9);
          line-height: 1.6;
          margin-bottom: 8px;
        }
        .verse-ref {
          font-size: 0.85rem;
          color: #A98667;
          font-weight: 600;
        }
        /* Content layout */
        .detail-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 60px 24px;
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 48px;
          align-items: start;
        }
        /* Themes */
        .themes-section h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.8rem;
          color: #FFFFFF;
          margin-bottom: 24px;
        }
        .theme-item {
          background: rgba(31, 42, 68, 0.5);
          border: 1px solid rgba(169, 134, 103, 0.12);
          border-radius: 12px;
          padding: 20px 22px;
          margin-bottom: 12px;
          transition: all 0.25s ease;
        }
        .theme-item:hover {
          border-color: rgba(169, 134, 103, 0.3);
          transform: translateX(4px);
        }
        .theme-ref {
          font-size: 0.75rem;
          font-weight: 700;
          color: #A98667;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .theme-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.1rem;
          color: #FFFFFF;
          margin-bottom: 6px;
        }
        .theme-desc {
          font-size: 0.875rem;
          color: rgba(240, 234, 226, 0.55);
          line-height: 1.5;
        }
        /* Thoughts section */
        .thoughts-section h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.8rem;
          color: #FFFFFF;
          margin-bottom: 8px;
        }
        .thoughts-subtitle {
          font-size: 0.9rem;
          color: rgba(240, 234, 226, 0.5);
          margin-bottom: 28px;
        }
        .thoughts-user {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .thoughts-avatar {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          color: #FFFFFF;
        }
        .thoughts-textarea {
          width: 100%;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 12px;
          padding: 16px;
          color: #FFFFFF;
          font-size: 0.95rem;
          font-family: 'Jost', sans-serif;
          resize: vertical;
          min-height: 120px;
          transition: all 0.2s ease;
          outline: none;
          line-height: 1.6;
        }
        .thoughts-textarea:focus {
          border-color: #A98667;
          background: rgba(169, 134, 103, 0.06);
          box-shadow: 0 0 0 3px rgba(169, 134, 103, 0.1);
        }
        .thoughts-textarea::placeholder { color: rgba(240, 234, 226, 0.3); }
        .thoughts-char {
          text-align: right;
          font-size: 0.78rem;
          color: rgba(240, 234, 226, 0.4);
          margin-top: 6px;
        }
        .thoughts-save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          color: #FFFFFF;
          border: none;
          border-radius: 10px;
          padding: 14px 28px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 12px;
        }
        .thoughts-save-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(169, 134, 103, 0.35);
        }
        .thoughts-save-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .thoughts-feedback {
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 16px;
        }
        .thoughts-feedback.success {
          background: rgba(39, 174, 96, 0.12);
          border: 1px solid rgba(39, 174, 96, 0.3);
          color: #27ae60;
        }
        .thoughts-feedback.error {
          background: rgba(231, 76, 60, 0.12);
          border: 1px solid rgba(231, 76, 60, 0.3);
          color: #e74c3c;
        }
        .thoughts-divider {
          border: none;
          border-top: 1px solid rgba(169, 134, 103, 0.1);
          margin: 32px 0;
        }
        .thoughts-list-title {
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(240, 234, 226, 0.4);
          margin-bottom: 16px;
        }
        .thought-card {
          background: rgba(31, 42, 68, 0.4);
          border: 1px solid rgba(169, 134, 103, 0.1);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 12px;
          animation: fadeInUp 0.3s ease;
        }
        .thought-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .thought-date {
          font-size: 0.78rem;
          color: rgba(240, 234, 226, 0.4);
        }
        .thought-delete {
          background: none;
          border: none;
          cursor: pointer;
          color: rgba(231, 76, 60, 0.5);
          font-size: 0.85rem;
          transition: color 0.2s;
          padding: 4px 8px;
          border-radius: 6px;
        }
        .thought-delete:hover {
          color: #e74c3c;
          background: rgba(231, 76, 60, 0.1);
        }
        .thought-content {
          font-size: 0.925rem;
          color: rgba(240, 234, 226, 0.8);
          line-height: 1.7;
          white-space: pre-wrap;
        }
        .no-thoughts {
          text-align: center;
          padding: 40px;
          color: rgba(240, 234, 226, 0.35);
          font-size: 0.9rem;
          font-style: italic;
        }
        @media (max-width: 900px) {
          .detail-content { grid-template-columns: 1fr; gap: 40px; }
        }
      `}</style>

      <div className="detail-page">
        {/* Hero */}
        <div className="detail-hero">
          <button className="detail-back" onClick={() => navigate('/moments')}>
            ← Retour aux moments
          </button>
          <div className="detail-hero-icon">{moment.icon}</div>
          <p className="detail-book">{moment.book}</p>
          <h1 className="detail-title">{moment.name}</h1>
          <p className="detail-intro">{moment.intro}</p>

          <div className="verse-block">
            <p className="verse-text">« {randomVerse?.text} »</p>
            <p className="verse-ref">— {randomVerse?.ref}</p>
          </div>
        </div>

        {/* Content grid */}
        <div className="detail-content">
          {/* Themes */}
          <div className="themes-section">
            <h2>Thèmes principaux</h2>
            {moment.themes.map((theme, i) => (
              <div className="theme-item" key={i}>
                <p className="theme-ref">{theme.ref}</p>
                <h3 className="theme-title">{theme.title}</h3>
                <p className="theme-desc">{theme.desc}</p>
              </div>
            ))}
          </div>

          {/* Thoughts */}
          <div className="thoughts-section">
            <h2>Ma réflexion personnelle</h2>
            <p className="thoughts-subtitle">Notez vos pensées, révélations et questions sur ce passage.</p>

            <div className="thoughts-user">
              <div className="thoughts-avatar">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <span style={{ fontSize: '0.9rem', color: 'rgba(240,234,226,0.7)' }}>{user?.name}</span>
            </div>

            {success && <div className="thoughts-feedback success">{success}</div>}
            {error && <div className="thoughts-feedback error">⚠ {error}</div>}

            <textarea
              className="thoughts-textarea"
              placeholder="Partagez vos réflexions, ce que ce passage vous inspire, vos questions..."
              value={newThought}
              onChange={(e) => setNewThought(e.target.value)}
              maxLength={2000}
            />
            <p className="thoughts-char">{newThought.length}/2000</p>

            <button
              className="thoughts-save-btn"
              onClick={handleSave}
              disabled={saving || !newThought.trim()}
            >
              {saving ? '⏳ Enregistrement...' : '✓ Enregistrer ma pensée'}
            </button>

            <hr className="thoughts-divider" />

            <p className="thoughts-list-title">Mes pensées précédentes ({thoughts.length})</p>

            {loadingThoughts ? (
              <div className="loader" />
            ) : thoughts.length === 0 ? (
              <div className="no-thoughts">
                Aucune pensée enregistrée pour ce moment.<br />
                Commencez par partager votre première réflexion ✦
              </div>
            ) : (
              thoughts.map((thought) => (
                <div className="thought-card" key={thought._id}>
                  <div className="thought-card-header">
                    <span className="thought-date">{formatDate(thought.createdAt)}</span>
                    <button className="thought-delete" onClick={() => handleDelete(thought._id)}>
                      Supprimer
                    </button>
                  </div>
                  <p className="thought-content">{thought.content}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MomentDetail;
